import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import { fn } from '@ember/helper';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import element from 'ember-element-helper/helpers/element';
import onKey from 'ember-keyboard/modifiers/on-key';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import or from 'ember-truth-helpers/helpers/or';
import BvMenuSection from 'volta/components/bv-menu/section';
import classNames from 'volta/helpers/class-names';
import { closest } from 'volta/utils/dom-utils';
import { ItemInnerClassName } from './item';

import type { ComponentLike, ContentValue, WithBoundArgs } from '@glint/template';
import type { TIntent } from 'volta/utils/style-utils';
import type { BvBadgeArgs } from 'volta/components/bv-badge';
import type { BvIconSignature } from 'volta/components/bv-icon';

type MenuItemColor = Extract<TIntent, 'danger' | 'success'>;

export interface IBvMenuItem {
  hasArrow?: boolean;
  isLoading?: boolean;
  active?: boolean;
  href?: string;
  model?: object;
  icon?: ContentValue | ComponentLike;
  iconColor?: BvIconSignature['Args']['color'];
  content?: ContentValue;
  suffix?: ContentValue | ComponentLike;
  color?: MenuItemColor;
  selected?: boolean;
  onClick?: (event: Event) => void;
  disabled?: boolean;
  tooltip?: string;
  title?: string;
  badge?: BvBadgeArgs;
  prefix?: ContentValue;
  shortcut?: ContentValue;
}

export interface IBvMenuSection {
  title?: string;
  items?: (IBvMenuItem | undefined)[];
}

export interface BvMenuSignature {
  Args: {
    // Collection of actions for list
    items?: (IBvMenuItem | undefined)[];
    // Collection of sectioned action items
    sections?: (IBvMenuSection | undefined)[];
    // Callback when any item is clicked or keypressed
    onAnyItemAction?: () => void;
  };
  Blocks: {
    default: [
      WithBoundArgs<
        typeof BvMenuSection,
        'hasMultipleSections' | 'onAnyItemAction' | 'onItemMouseEnter'
      >
    ];
  };
}

const ItemInnerEnabledSelector = `.${ItemInnerClassName}:not([disabled])`;

export default class BvMenuComponent extends Component<BvMenuSignature> {
  @ref('rootElement')
  rootElement: HTMLDivElement | null = null;

  @tracked
  selected: number = -1;

  /**
   * It takes the sections array and adds the items array to it.
   * @returns The finalSections array.
   */
  @cached
  get finalSections() {
    const { items = [], sections = [] } = this.args;
    const validItems = items.filter(Boolean);
    const validSections = sections.reduce<IBvMenuSection[]>((acc, s) => {
      if (s?.items?.length) {
        acc.push({ ...s, items: s.items.filter(Boolean) });
      }
      return acc;
    }, []);
    const finalSections = [];

    if (validItems.length > 0) {
      finalSections.push({ items: validItems });
    }

    assert(
      `bevolta::bv-menu - sections must be an array, you passed ${sections}`,
      isArray(sections)
    );

    if (validSections.length > 0) {
      finalSections.push(...validSections);
    }

    return finalSections;
  }

  get hasMultipleSections() {
    return this.finalSections.length > 1;
  }

  get itemsNodes() {
    return (this.rootElement?.querySelectorAll(ItemInnerEnabledSelector) ??
      []) as NodeListOf<HTMLDivElement>;
  }

  // Actions
  // ~~~~~

  handleChangeSelected = (direction: 'up' | 'down', event: KeyboardEvent) => {
    const isInRootElement = this.rootElement?.contains(event.target as Node);
    if (!isInRootElement) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const { selected, itemsNodes } = this;
    let index = 0;
    let newIndex = 0;

    if (direction === 'down') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index === itemsNodes.length - 1 || selected < 0 ? 0 : index + 1;
    } else if (direction === 'up') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index ? index - 1 : itemsNodes.length - 1;
    } else {
      this.selected = 0;
    }

    const newItem = itemsNodes[newIndex];

    if (newItem && typeof newIndex === 'number') {
      this.selected = newIndex;
      newItem.scrollIntoView({
        behavior: 'smooth',
        block: newIndex ? 'center' : 'end'
      });
      newItem.focus();
    }
  };

  handleSelect = (event: KeyboardEvent) => {
    const isInRootElement = this.rootElement?.contains(event.target as Node);
    if (!isInRootElement) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const { selected, itemsNodes } = this;
    let index = 0;

    itemsNodes.forEach((_, i) => {
      if (i === selected) {
        index = i;
      }
    });

    const innerInteractiveElement = itemsNodes[index];

    if (innerInteractiveElement) {
      innerInteractiveElement.click();
    }
  };

  handleItemMouseEnter = (event: Event) => {
    if (event.currentTarget) {
      const item = closest(event.currentTarget as HTMLElement, ItemInnerEnabledSelector);

      if (item) {
        const { itemsNodes } = this;
        // Find the index of the hovered item
        const index = Array.from(itemsNodes).indexOf(item as HTMLDivElement);
        this.selected = index;
        item.focus();
      }
    }
  };

  handleTab = (event: KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  <template>
    {{#if (or this.finalSections.length (has-block))}}
      {{#let (element (if this.hasMultipleSections 'ul' 'div')) as |Tag|}}
        <Tag
          class={{classNames
            'bv-menu'
            (if this.hasMultipleSections 'bv-menu--has-multiple-sections')
          }}
          role={{if this.hasMultipleSections 'listbox'}}
          tabindex='-1'
          {{createRef 'rootElement' bucket=this}}
          {{onKey 'ArrowDown' (fn this.handleChangeSelected 'down')}}
          {{onKey 'ArrowUp' (fn this.handleChangeSelected 'up')}}
          {{onKey 'Enter' this.handleSelect}}
          {{onKey 'Space' this.handleSelect}}
          {{onKey 'Tab' this.handleTab}}
        >
          {{#let
            (component
              BvMenuSection
              hasMultipleSections=this.hasMultipleSections
              onAnyItemAction=@onAnyItemAction
              onItemMouseEnter=this.handleItemMouseEnter
            )
            as |Section|
          }}
            {{#each this.finalSections as |section|}}
              {{#if section.items.length}}
                <Section @title={{section.title}} @items={{section.items}} />
              {{/if}}
            {{/each}}
            {{#if (has-block)}}
              {{yield Section}}
            {{/if}}
          {{/let}}
        </Tag>
      {{/let}}
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvMenu: typeof BvMenuComponent;
    'bv-menu': typeof BvMenuComponent;
  }
}
